import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Paper, Typography, Box, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, CircularProgress,
  Alert, TextField, InputAdornment, Button
} from '@mui/material';
import { Visibility, Edit, Delete, Search, Add } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import AppHeader from '../../components/AppHeader';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function AngebotListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [angebote, setAngebote] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchAngebote();
  }, []);

  const fetchAngebote = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/angebote`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Backend sendet {angebote: [...]}
      const angeboteData = response.data.angebote || response.data || [];
      
      // Filter by user role - nur Admin sieht alles
      let filteredData = angeboteData;
      if (user?.role !== 'admin') {
        // Normale Monteure sehen nur ihre eigenen Angebote
        filteredData = angeboteData.filter(angebot => 
          angebot.monteur === user?.username || angebot.monteur === user?.full_name
        );
      }
      
      // Sort by date (newest first)
      const sorted = filteredData.sort((a, b) => 
        new Date(b.created_at || b.datum) - new Date(a.created_at || a.datum)
      );
      setAngebote(sorted);
      setError('');
    } catch (err) {
      console.error('Error fetching angebote:', err);
      setError('Fehler beim Laden der Angebote');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    // Nur 1 Bestätigungsdialog, kein weiterer nach Löschung
    if (!window.confirm('Möchten Sie dieses Angebot wirklich löschen?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/angebot/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Erfolgreich gelöscht - kein Alert, nur Liste aktualisieren
      setAngebote(angebote.filter(a => a._id !== id));
    } catch (err) {
      console.error('Error deleting angebot:', err);
      alert('Fehler beim Löschen des Angebots');
    }
  };

  const handleView = (angebot) => {
    navigate(`/angebot/view/${angebot._id}`, { state: { angebot } });
  };

  const handleEdit = (angebot) => {
    // Navigate to form with existing data
    navigate(`/angebot/form`, { 
      state: { 
        customer: {
          ku_nr: angebot.ku_nr,
          name: angebot.kunde_name,
          strasse: angebot.kunde_strasse,
          plz: angebot.kunde_plz,
          ort: angebot.kunde_ort,
          email: angebot.kunde_email,
          mobilfunk: angebot.kunde_telefon
        },
        existingAngebot: angebot
      }
    });
  };

  const filteredAngebote = angebote.filter(angebot => {
    const query = searchQuery.toLowerCase();
    return (
      angebot.kunde_name?.toLowerCase().includes(query) ||
      angebot.ku_nr?.toLowerCase().includes(query) ||
      angebot.monteur?.toLowerCase().includes(query) ||
      angebot.datum?.includes(query)
    );
  });

  const getStatusColor = (status) => {
    return status === 'submitted' ? 'success' : 'warning';
  };

  const getStatusLabel = (status) => {
    return status === 'submitted' ? 'Versendet' : 'Entwurf';
  };

  if (loading) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <AppHeader title="Angebote" showBack={true} backTo="/dashboard" />
        <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppHeader title="Angebote" showBack={true} backTo="/dashboard" />

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Header with search and new button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <TextField
            placeholder="Suchen nach Kunde, Nr., Monteur..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ width: '400px' }}
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/angebot/customer-search')}
          >
            Neues Angebot
          </Button>
        </Box>

        {/* Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell><strong>Angebot-Nr.</strong></TableCell>
                <TableCell><strong>Datum</strong></TableCell>
                <TableCell><strong>Kunde</strong></TableCell>
                <TableCell><strong>Kundennummer</strong></TableCell>
                <TableCell><strong>Monteur</strong></TableCell>
                <TableCell><strong>Räume</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Aktionen</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAngebote.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      {searchQuery ? 'Keine Angebote gefunden' : 'Noch keine Angebote erstellt'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAngebote.map((angebot) => (
                  <TableRow key={angebot._id} hover>
                    <TableCell><strong>#{angebot.angebot_nr || 'N/A'}</strong></TableCell>
                    <TableCell>{angebot.datum}</TableCell>
                    <TableCell>{angebot.kunde_name}</TableCell>
                    <TableCell>{angebot.ku_nr}</TableCell>
                    <TableCell>{angebot.monteur}</TableCell>
                    <TableCell>{angebot.raeume?.length || 0}</TableCell>
                    <TableCell>
                      <Chip 
                        label={getStatusLabel(angebot.status)} 
                        color={getStatusColor(angebot.status)} 
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton 
                        size="small" 
                        color="primary" 
                        onClick={() => handleView(angebot)}
                        title="Anzeigen"
                      >
                        <Visibility />
                      </IconButton>
                      {angebot.status === 'draft' && (
                        <IconButton 
                          size="small" 
                          color="secondary" 
                          onClick={() => handleEdit(angebot)}
                          title="Bearbeiten"
                        >
                          <Edit />
                        </IconButton>
                      )}
                      {user?.role === 'admin' && (
                        <IconButton 
                          size="small" 
                          color="error" 
                          onClick={() => handleDelete(angebot._id)}
                          title="Löschen"
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Insgesamt {filteredAngebote.length} {filteredAngebote.length === 1 ? 'Angebot' : 'Angebote'}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
